import React, { useState } from "react";
import ScrollModal from "@/components/common/scroll-modal";
import ConfirmationModalBase from "@/components/common/modals/confirmation-modal-base";
import {
  Heading,
  Button,
  Badge,
  Avatar,
  AvatarFallback,
  Icon,
  Switch,
  useToast,
} from "@elaniintc/goes-design-system";
import TextField, { TextAreaField } from "@/components/common/text-field";
import {
  useCreateTag,
  useUpdateTag,
  useDeleteTag,
} from "@/queries/tags/tags.api";
import type { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { newTagSchema, editTagFormSchema } from "@/schemas/tags";
import type { NewTagFormData, EditTagFormSchemaData } from "@/schemas/tags";
import type { TagType } from "@/types/entities/tags";

const MAX_FILE_SIZE_MB = 1; // 1 MB max
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface NewTagModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editData?: TagType;
}

// Custom hook for image upload logic
function useImageUpload(
  maxFileSizeBytes: number,
  cb: (file: File | null) => void,
) {
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [invalidImage, setInvalidImage] = useState<{
    name: string;
    size: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const addImage = (file: File) => {
    // Clear any previous invalid image state first
    setInvalidImage(null);
    setImage(null);
    setImagePreview(null);

    // Validar tipo de archivo
    if (
      !file.type.startsWith("image/jpeg") &&
      !file.type.startsWith("image/jpg")
    ) {
      setInvalidImage({ name: file.name, size: file.size });
      cb(null); // Clear the form value for invalid files
      return;
    }

    // Validar tamaño
    if (file.size > maxFileSizeBytes) {
      setInvalidImage({ name: file.name, size: file.size });
      cb(null); // Clear the form value for invalid files
      return;
    }

    setImage(file);
    cb(file);

    // Crear preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      addImage(file);
    }
    // Clear the input value to allow re-selecting the same file
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      addImage(file);
    }
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview(null);
    setInvalidImage(null);
    cb(null);
  };

  return {
    image,
    setImage,
    imagePreview,
    setImagePreview,
    invalidImage,
    setInvalidImage,
    isDragging,
    setIsDragging,
    handleImageChange,
    handleDrop,
    handleRemoveImage,
  };
}

export default function NewTagModal({
  open,
  onClose,
  onSuccess,
  editData,
}: NewTagModalProps) {
  const isEditing = !!editData;
  const createMutation = useCreateTag();
  const updateMutation = useUpdateTag();
  const deleteMutation = useDeleteTag();
  const mutation = isEditing ? updateMutation : createMutation;
  const { showToast } = useToast();

  // Estado para el modal de confirmación de eliminación
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Estado para el modal de confirmación de guardar cambios
  const [isSaveConfirmModalOpen, setIsSaveConfirmModalOpen] = useState(false);

  // Estado para guardar los datos del formulario temporalmente
  const [pendingFormData, setPendingFormData] = useState<
    NewTagFormData | EditTagFormSchemaData | null
  >(null);

  const initialState = isEditing
    ? {
        name: editData.name,
        description: editData.description || "",
        active: editData.active,
        image: undefined as File | undefined,
      }
    : {
        name: "",
        description: "",
        active: false, // Cambiado a false por defecto para permitir selección del usuario
        image: undefined as File | undefined,
      };

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    setError,
    clearErrors,
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(isEditing ? editTagFormSchema : newTagSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: initialState,
  });

  const description = watch("description");
  const active = watch("active");

  // Asegurar que el valor inicial se establezca correctamente
  React.useEffect(() => {
    if (!isEditing) {
      setValue("active", false, { shouldValidate: true });
    }
  }, [isEditing, setValue]);

  // Use custom hook for image logic
  const {
    image,
    setImage,
    imagePreview,
    setImagePreview,
    invalidImage,
    setInvalidImage,
    isDragging,
    setIsDragging,
    handleImageChange,
    handleDrop,
    handleRemoveImage,
  } = useImageUpload(MAX_FILE_SIZE_BYTES, (file) => {
    setValue("image", file || undefined);
  });

  // Reset form cuando editData cambia
  React.useEffect(() => {
    if (isEditing && editData) {
      const newValues = {
        name: editData.name,
        description: editData.description || "",
        active: editData.active,
        image: undefined as File | undefined,
      };

      // Reset form con nuevos valores
      reset(newValues);

      // Set image preview si existe
      if (
        editData.image_url ||
        editData.files?.[(editData.files.length || 1) - 1]?.url
      ) {
        setImagePreview(
          editData.image_url ||
            editData.files?.[(editData.files.length || 1) - 1]?.url ||
            null,
        );
      } else {
        setImagePreview(null);
      }
    } else if (!isEditing) {
      // Reset para modo creación
      const newValues = {
        name: "",
        description: "",
        active: false,
        image: undefined as File | undefined,
      };
      reset(newValues);
      setImagePreview(null);
      setImage(null);
      setInvalidImage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, editData, reset, setImagePreview, setImage]);

  const onSubmit = async (data: NewTagFormData | EditTagFormSchemaData) => {
    if (isEditing) {
      // En modo edición, mostrar modal de confirmación
      setPendingFormData(data);
      setIsSaveConfirmModalOpen(true);
    } else {
      // En modo creación, proceder directamente
      await executeSubmit(data);
    }
  };

  const executeSubmit = async (
    data: NewTagFormData | EditTagFormSchemaData,
  ) => {
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({
          id: editData.id,
          payload: {
            name: data.name,
            description: data.description,
            active: data.active,
            image: data.image,
          },
        });

        // Toast para actualización exitosa
        showToast({
          title: "Cambios guardados",
          description: "Los cambios se guardaron exitosamente.",
          options: {
            type: "success",
            duration: 4000,
            customIcon: (
              <Avatar size="xs" shape="square">
                <AvatarFallback color="action" shape="square" type={"ghost"}>
                  <Icon type="fas" name="save" />
                </AvatarFallback>
              </Avatar>
            ),
          },
        });
      } else {
        await createMutation.mutateAsync({
          name: data.name,
          description: data.description,
          active: data.active,
          image: data.image,
        });
      }

      clearErrors();
      handleClose();
      onSuccess();
    } catch (e) {
      console.error("Error al enviar el formulario:", e);
      const error = e as AxiosError<{
        message?: string;
        errors?: { [key: string]: string[] };
      }>;

      // Manejo de error por nombre duplicado
      const apiMessage = error?.response?.data?.message?.toLowerCase?.() ?? "";
      const nameErrorMsg =
        error?.response?.data?.errors?.name?.[0]?.toLowerCase?.() ?? "";

      if (
        apiMessage.includes("validation.unique") ||
        nameErrorMsg.includes("validation.unique")
      ) {
        setError("name", {
          type: "duplicate",
          message: "El nombre ya está en uso. Usa uno diferente.",
        });
      } else {
        // Toast de error específico para actualización fallida
        if (isEditing) {
          showToast({
            title: "Error al guardar",
            description:
              "Los cambios no pudieron ser guardados. Por favor vuelve a ingresar a la sección e inténtalo de nuevo.",
            options: {
              type: "error",
              duration: 4000,
              customIcon: (
                <Avatar size="xs" shape="square">
                  <AvatarFallback color="danger" shape="square">
                    <Icon type="fas" name="save-times" />
                  </AvatarFallback>
                </Avatar>
              ),
            },
          });
        }

        setError("name", {
          type: "server",
          message:
            error?.message ||
            error?.response?.data?.message ||
            `Error al ${isEditing ? "actualizar" : "crear"} la etiqueta`,
        });
      }
    }
  };

  const handleClose = () => {
    // Reset formulario a estado inicial limpio
    reset({
      name: "",
      description: "",
      active: false,
      image: undefined,
    });

    // Limpiar estados de imagen
    setImage(null);
    setImagePreview(null);
    setInvalidImage(null);

    onClose();
  };

  const confirmSaveChanges = async () => {
    if (pendingFormData) {
      setIsSaveConfirmModalOpen(false);
      await executeSubmit(pendingFormData);
      setPendingFormData(null);
    }
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (editData?.id) {
      deleteMutation.mutate(editData.id, {
        onSuccess: () => {
          showToast({
            title: "Etiqueta eliminada",
            description: "La etiqueta fue eliminada exitosamente.",
            options: {
              type: "error",
              duration: 4000,
              customIcon: (
                <Avatar size="xs" shape="square">
                  <AvatarFallback color="danger" shape="square" type={"ghost"}>
                    <Icon type="fas" name="trash" />
                  </AvatarFallback>
                </Avatar>
              ),
            },
          });
          setIsDeleteModalOpen(false);
          onSuccess();
          handleClose();
        },
        onError: () => {
          showToast({
            title: "Error al eliminar",
            description:
              "La etiqueta no pudo ser eliminada. Por favor inténtalo nuevamente.",
            options: {
              type: "error",
              duration: 4000,
              customIcon: (
                <Avatar size="xs" shape="square">
                  <AvatarFallback color="danger" shape="square">
                    <Icon type="fas" name="exclamation-triangle" />
                  </AvatarFallback>
                </Avatar>
              ),
            },
          });
        },
      });
    }
  };

  return (
    <>
      <ScrollModal
        isOpen={open}
        onClose={handleClose}
        modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col"
        header={
          <div className="flex items-center justify-between px-8 py-3">
            <Heading variant="h5" className="!mb-0">
              {isEditing ? "Editar Etiqueta" : "Nueva Etiqueta"}
            </Heading>
            <Button
              type="button"
              onClick={handleClose}
              variant="quiet"
              aria-label="Cerrar"
              className="w-8 h-8 cursor-pointer"
            >
              <Icon type="far" name="times" className="text-sm text-body" />
            </Button>
          </div>
        }
        footer={
          <div className="w-full py-3 px-8">
            {isEditing ? (
              <div className="w-full flex items-center justify-between gap-3">
                <div className="flex gap-3">
                  <Button
                    size="sm"
                    type="button"
                    variant="danger-tertiary"
                    onClick={handleDelete}
                    disabled={
                      mutation.isPending ||
                      Boolean(
                        editData?.processes_count &&
                        editData.processes_count > 0,
                      )
                    }
                    className="min-w-[120px] cursor-pointer"
                  >
                    Eliminar
                  </Button>
                  {/* {onViewHistory && (
                    <Button
                      size="sm"
                      type="button"
                      variant="tertiary"
                      onClick={() => editData && onViewHistory(editData.id)}
                      disabled={mutation.isPending}
                      className="min-w-[120px] cursor-pointer"
                    >
                      Ver historial
                    </Button>
                  )} */}
                </div>

                {/* Botones Cancelar y Guardar alineados a la derecha */}
                <div className="flex gap-3">
                  <Button
                    variant="tertiary"
                    size="sm"
                    type="button"
                    onClick={handleClose}
                    disabled={mutation.isPending}
                    className="min-w-[120px] cursor-pointer"
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    type="submit"
                    className="min-w-[140px] cursor-pointer disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-none disabled:shadow-none"
                    disabled={!isValid || mutation.isPending}
                    form="tag-form"
                  >
                    {mutation.isPending ? "Actualizando..." : "Guardar cambios"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="w-full grid grid-cols-1 tablet:grid-cols-2 gap-3">
                <Button
                  variant="tertiary"
                  size="sm"
                  type="button"
                  onClick={handleClose}
                  disabled={mutation.isPending}
                  className="min-w-[120px] cursor-pointer"
                >
                  Cancelar
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  type="submit"
                  className="min-w-[140px] cursor-pointer disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-none disabled:shadow-none"
                  disabled={!isValid || mutation.isPending}
                  form="tag-form"
                >
                  {mutation.isPending ? "Creando..." : "Crear etiqueta"}
                </Button>
              </div>
            )}
          </div>
        }
      >
        <form
          id="tag-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 min-h-0 px-4 sm:px-8"
        >
          {/* Contenido scrollable */}
          <div className="flex flex-col gap-6 pb-6">
            {/* Detalles */}
            <div>
              <div className="flex flex-col gap-4">
                <TextField
                  label="Nombre"
                  required
                  maxLength={50}
                  error={errors.name?.message || ""}
                  placeholder="Escribe el nombre de la etiqueta"
                  {...register("name")}
                />
                <TextAreaField
                  label="Descripción"
                  required
                  maxLength={500}
                  placeholder="Escribe la descripción de la etiqueta"
                  rows={3}
                  className="relative text-body-loud"
                  error={errors.description?.message || ""}
                  {...register("description")}
                  count={description?.length || 0}
                  limit={500}
                />

                {/* Status Field */}
                <div className="flex flex-col items-start gap-1 w-full">
                  <label className="flex items-center w-full font-semibold text-sm leading-6 text-body-loud">
                    Estado
                    <span className="font-semibold text-sm leading-6 text-danger ml-1">
                      *
                    </span>
                  </label>
                  <div className="flex items-center gap-2 w-full">
                    <Switch
                      checked={active}
                      onCheckedChange={(checked) => {
                        setValue("active", checked, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                      id="tag-status-switch"
                    />
                    <label
                      htmlFor="tag-status-switch"
                      className="flex-1 text-base leading-7 font-normal cursor-pointer text-body"
                    >
                      {active ? "Activa" : "Inactiva"}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Carga de imagen */}
            <div>
              <Heading variant="h6" className="mb-2 text-body-loud">
                Imagen
              </Heading>

              {/* Solo mostrar zona de carga si NO hay imagen existente */}
              {!imagePreview && (
                <div
                  className={`border-2 border-dashed rounded-xl p-4 flex flex-col items-center justify-center gap-2 transition-colors duration-200 ${
                    isDragging
                      ? "border-neutral bg-surface-action-accent-quiet"
                      : "border-neutral"
                  }`}
                  style={{ minHeight: 120 }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(false);
                  }}
                  onDrop={handleDrop}
                >
                  {isDragging ? (
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Avatar shape="square">
                        <AvatarFallback color="action" shape="square">
                          <Icon
                            type="far"
                            name="arrow-up"
                            className="text-base text-action-quiet mb-1 w-3"
                          />
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-action-quiet font-medium">
                        Suelta tu imagen aquí
                      </span>
                    </div>
                  ) : (
                    <>
                      <span className="text-body text-sm">
                        Arrastra y Suelta o{" "}
                        <label className="text-action cursor-pointer font-medium">
                          Selecciona una imagen
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg"
                            onChange={handleImageChange}
                            className="hidden"
                          />
                        </label>
                      </span>
                      <span className="text-xs text-body-quiet">
                        Sube una imagen .jpg (máximo 1MB)
                      </span>
                    </>
                  )}
                  {errors.image?.message && (
                    <span className="text-danger text-xs">
                      {errors.image?.message || ""}
                    </span>
                  )}
                </div>
              )}

              {/* Preview de la imagen */}
              {imagePreview && (
                <div className="flex flex-col gap-3">
                  {/* Imagen válida */}
                  <div className="w-full text-body flex items-center justify-between border rounded-lg p-3 border-neutral">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div
                        className={`rounded-lg overflow-hidden bg-neutral-object ${
                          !image && isEditing ? "w-7 h-7" : "w-12 h-12"
                        }`}
                      >
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className="truncate overflow-hidden text-sm min-w-0 flex-1">
                        {image?.name || "Imagen actual"}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {/* Badge para archivo nuevo subido */}
                      {image && (
                        <Badge variant="neutral" size="xs" shape="pill">
                          {(image.size / 1024 / 1024).toFixed(2)} MB
                        </Badge>
                      )}
                      {/* Badge para imagen existente en modo edición */}
                      {!image && isEditing && editData?.files?.[0]?.size && (
                        <Badge variant="neutral" size="xs" shape="pill">
                          {(editData.files[0].size / 1024 / 1024).toFixed(2)} MB
                        </Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="xs"
                        type="button"
                        className="cursor-pointer"
                        onClick={handleRemoveImage}
                      >
                        <Icon
                          type="far"
                          name="trash"
                          className="text-current"
                        />
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Mensaje de error para imagen inválida */}
              {invalidImage && !imagePreview && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-danger flex items-center gap-1 px-3 py-2 bg-danger-surface rounded-lg">
                    <Icon
                      type="far"
                      name="circle-xmark"
                      className="text-danger text-sm"
                    />
                    {invalidImage.size > MAX_FILE_SIZE_BYTES
                      ? "Imagen muy pesada, máximo 1MB permitido."
                      : "Solo se permiten archivos JPG."}
                  </span>
                </div>
              )}
            </div>
          </div>
        </form>
      </ScrollModal>

      {/* Modal de confirmación para guardar cambios */}
      <ConfirmationModalBase
        isOpen={isSaveConfirmModalOpen}
        onClose={() => {
          setIsSaveConfirmModalOpen(false);
          setPendingFormData(null);
        }}
        onAccept={confirmSaveChanges}
        title="Guardar cambios"
        description="¿Estás seguro de que deseas guardar los cambios realizados?"
        acceptText="Guardar cambios"
        cancelText="Cancelar"
        acceptVariant="default"
        isLoading={updateMutation.isPending}
      />

      {/* Modal de confirmación para eliminar */}
      <ConfirmationModalBase
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onAccept={confirmDelete}
        title="Eliminar etiqueta"
        description={`¿Estás seguro de que deseas eliminar la etiqueta "${editData?.name}"? Esta acción no se puede deshacer.`}
        acceptText="Eliminar"
        cancelText="Cancelar"
        acceptVariant="danger-primary"
        isLoading={deleteMutation.isPending}
      />
    </>
  );
}
